import * as React from "react";
import {
  Card,
  Text,
  makeStyles,
  tokens,
  Checkbox,
} from "@fluentui/react-components";
import { DiscussionTypeOption } from "../types/discussionTypes";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: tokens.spacingHorizontalS,
  },
  card: {
    padding: tokens.spacingVerticalM,
    cursor: "pointer",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    border: `2px solid transparent`,
    transition: "all 0.2s ease",
    ":hover": {
      boxShadow: tokens.shadow4,
    },
  },
  cardSelected: {
    border: `2px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
  },
});

interface TagSelectorProps {
  title: string;
  tags: DiscussionTypeOption[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

const TagSelector: React.FC<TagSelectorProps> = ({
  title,
  tags,
  selectedTags,
  onTagsChange,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();

  const handleTagToggle = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      onTagsChange(selectedTags.filter((t) => t !== tagId));
    } else {
      onTagsChange([...selectedTags, tagId]);
    }
  };

  return (
    <div>
      <Text
        className={typography.sectionTitle}
        style={{ marginBottom: tokens.spacingVerticalS, display: "block" }}
      >
        {title}
      </Text>
      <div className={styles.container}>
        {tags.map((tag) => {
          const Icon = tag.icon;
          const isSelected = selectedTags.includes(tag.id);

          return (
            <Card
              key={tag.id}
              className={`${styles.card} ${isSelected ? styles.cardSelected : ""}`}
              onClick={() => handleTagToggle(tag.id)}
            >
              <Checkbox
                checked={isSelected}
                onChange={() => handleTagToggle(tag.id)}
              />
              <div
                className={styles.iconContainer}
                style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
              >
                <Icon />
              </div>
              <div className={styles.textContainer}>
                <Text className={typography.bodySmallStrong}>{tag.name}</Text>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default TagSelector;
